import { useState, type ReactNode } from 'react'
import type { DeviceInfo } from '../../common/enums'
import type { FleetSignalRow } from '../../types/signalStrength'
import { signalBars } from '../../utils/signalBars'
import SignalBars, { SignalLegend } from '../charts/SignalBars'
import { marginLabel, sortWeakSignal } from './WeakSignalList'

/** Strips shown in each column before the worst list's "more" toggle. */
const STRIP_DEVICES = 10

/**
 * The weak-signal panel's daily strips, above its table: the worst devices
 * beside the best, one strip each, so a bad install's days can be read
 * against a good one's. Laid out like the Missing Readings card's best/worst
 * strips, and drawn with the device card's own signal bars, so a device
 * looks the same everywhere.
 *
 * Worst is exactly the weak list below, in its order. Best is the strongest
 * margin of everything else heard in the window, more base stations first on
 * a tie - a device heard by one station is never "best" over one heard by two
 * at the same level.
 */
export default function SignalStrips({
  weakRows,
  allRows,
  days,
  devices,
  showAccount,
  onOpen,
}: {
  /** The critical and weak devices, as the list above shows them. */
  weakRows: FleetSignalRow[]
  /** Every device, for the best list. */
  allRows: FleetSignalRow[]
  days: number
  devices: Record<string, DeviceInfo>
  showAccount: boolean
  onOpen: (sigfoxId: string) => void
}) {
  const [showAll, setShowAll] = useState(false)

  // An API that predates the series sends no `daily`; draw nothing rather
  // than a window of empty stubs that would read as "never heard".
  if (!allRows.some((r) => r.daily)) return null

  const worst = sortWeakSignal(weakRows)
  const worstIds = new Set(worst.map((r) => r.sigfoxId))
  const best = allRows
    .filter((r) => !worstIds.has(r.sigfoxId) && r.summary.marginDb !== null)
    .sort(
      (a, b) =>
        (b.summary.marginDb ?? 0) - (a.summary.marginDb ?? 0) ||
        b.summary.stations.length - a.summary.stations.length ||
        a.sigfoxId.localeCompare(b.sigfoxId)
    )
    .slice(0, STRIP_DEVICES)

  const now = new Date()
  const axis = signalBars([], days, now)
  // Capture began partway into a long window; say so, as the device card does,
  // so the empty stubs before it are not read as every device going quiet.
  const firstDay = allRows
    .flatMap((r) => r.daily ?? [])
    .reduce<string | null>((min, d) => (min === null || d.day < min ? d.day : min), null)
  const startsLate = firstDay !== null && axis.length > 0 && firstDay > axis[0].day
  const more = worst.slice(STRIP_DEVICES)

  const row = ({ sigfoxId, summary, daily }: FleetSignalRow) => {
    const device = devices[sigfoxId]
    return (
      <li key={sigfoxId}>
        <button
          type="button"
          onClick={() => onOpen(sigfoxId)}
          className="flex w-full flex-col gap-1.5 rounded px-1 py-2 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:flex-row sm:items-center sm:gap-4"
        >
          <span className="min-w-0 sm:w-40 sm:flex-shrink-0">
            <span className="block truncate text-sm font-medium text-gray-900">{device?.deviceName || sigfoxId}</span>
            <span className="block truncate text-xs text-gray-400">
              {sigfoxId}
              {showAccount && device?.accountName && ` · ${device.accountName}`}
            </span>
          </span>
          <span className="min-w-0 flex-1">
            <SignalBars bars={signalBars(daily ?? [], days, now)} heightClass="h-7" stretch showDates={false} />
          </span>
          <span className="whitespace-nowrap text-sm tabular-nums text-gray-600 sm:w-32 sm:flex-shrink-0 sm:text-right">
            {marginLabel(summary.marginDb)}
          </span>
        </button>
      </li>
    )
  }

  const column = (headingId: string, heading: string, list: FleetSignalRow[], footer?: ReactNode) =>
    list.length === 0 ? null : (
      <div className="min-w-0">
        <h4 id={headingId} className="mb-1 text-xs font-medium text-gray-500">
          {heading}
        </h4>
        <div aria-hidden="true" className="hidden px-1 text-[11px] tabular-nums text-gray-500 sm:flex sm:gap-4">
          <span className="sm:w-40 sm:flex-shrink-0" />
          <span className="flex flex-1 justify-between">
            <span>{axis[0]?.day}</span>
            <span>{axis[axis.length - 1]?.day}</span>
          </span>
          <span className="sm:w-32 sm:flex-shrink-0" />
        </div>
        <ul aria-labelledby={headingId} className="divide-y divide-gray-100">
          {list.slice(0, STRIP_DEVICES).map(row)}
        </ul>
        {footer}
      </div>
    )

  const moreList = more.length > 0 && (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        aria-expanded={showAll}
        className="text-xs font-medium text-blue-600 hover:text-blue-700"
      >
        {showAll ? 'Hide' : 'Show'} {more.length} more device{more.length === 1 ? '' : 's'}
      </button>
      {showAll && (
        <ul aria-label="More devices" className="mt-2 divide-y divide-gray-100">
          {more.map(row)}
        </ul>
      )}
    </div>
  )

  return (
    <section aria-label="Daily signal" className="mb-5 border-b border-gray-100 pb-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Daily signal · last {days} day{days === 1 ? '' : 's'} · median per day
          {startsLate && ` · tracking started ${firstDay}; earlier days have no data`}
        </p>
        <SignalLegend />
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
        {column('signal-best-heading', `Best ${best.length} devices`, best)}
        {column('signal-worst-heading', `Worst ${Math.min(worst.length, STRIP_DEVICES)} devices`, worst, moreList)}
      </div>
    </section>
  )
}
